import api from './api';
import { API_ROUTES } from '../config/api';

export interface Promotion {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  database_name: string;
  filters: Record<string, any>;
  article_ids?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePromotionData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  database_name: string;
  filters: Record<string, any>;
  article_ids?: string[];
}

export interface UpdatePromotionData extends Partial<CreatePromotionData> {
  is_active?: boolean;
  article_ids?: string[];
}

class PromotionsService {
  
  /**
   * Get all promotions for a specific database
   */
  async getPromotions(databaseId: number | string): Promise<Promotion[]> {
    try {
      const response = await api.get(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.LIST(databaseId));
      return response.data;
    } catch (error) {
      console.error(`Error fetching promotions for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific promotion by ID
   */
  async getPromotion(databaseId: number | string, id: number): Promise<Promotion> {
    try {
      const response = await api.get(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.DETAIL(databaseId, id));
      return response.data;
    } catch (error) {
      console.error(`Error fetching promotion ${id} for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new promotion
   */
  async createPromotion(databaseId: number | string, data: Omit<CreatePromotionData, 'database_name'>): Promise<Promotion> {
    try {
      const promotionData = {
        ...data,
        database_name: databaseId  // Backend will resolve this from database_id
      };
      const response = await api.post(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.CREATE(databaseId), promotionData);
      return response.data;
    } catch (error) {
      console.error(`Error creating promotion for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing promotion
   */
  async updatePromotion(databaseId: number | string, id: number, data: Omit<UpdatePromotionData, 'database_name'>): Promise<Promotion> {
    try {
      const updateData = {
        ...data,
        database_name: databaseId  // Backend will resolve this from database_id
      };
      
      const response = await api.put(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.UPDATE(databaseId, id), updateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating promotion ${id} for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Partially update a promotion (PATCH)
   */
  async patchPromotion(databaseId: number | string, id: number, data: Partial<Omit<UpdatePromotionData, 'database_name'>>): Promise<Promotion> {
    try {
      const response = await api.patch(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.UPDATE(databaseId, id), data);
      return response.data;
    } catch (error) {
      console.error(`Error patching promotion ${id} for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a promotion
   */
  async deletePromotion(databaseId: number | string, id: number): Promise<void> {
    try {
      await api.delete(API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.DELETE(databaseId, id));
    } catch (error) {
      console.error(`Error deleting promotion ${id} for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Cancel a promotion (set is_active to false)
   */
  async cancelPromotion(databaseId: number | string, id: number): Promise<Promotion> {
    return this.patchPromotion(databaseId, id, { is_active: false });
  }

  /**
   * Reactivate a promotion (set is_active to true)
   */
  async reactivatePromotion(databaseId: number | string, id: number): Promise<Promotion> {
    return this.patchPromotion(databaseId, id, { is_active: true });
  }

  /**
   * Get active promotions only for a specific database
   */
  async getActivePromotions(databaseId: number | string): Promise<Promotion[]> {
    try {
      const promotions = await this.getPromotions(databaseId);
      return promotions.filter(promotion => promotion.is_active);
    } catch (error) {
      console.error(`Error fetching active promotions for database ID ${databaseId}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
const promotionsService = new PromotionsService();
export default promotionsService; 